import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { type NextRequest } from "next/server"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Adicionar função para formatar tamanho de arquivos
export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return "0 Bytes"

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
}

// Add the formatDate function to the utils.ts file

export function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

const TRANSIENT_PANEL_HOST_SUFFIXES = [
  ".ngrok-free.dev",
  ".ngrok-free.app",
  ".ngrok.app",
  ".ngrok.io",
  ".trycloudflare.com",
  ".loca.lt",
  ".localhost.run",
  ".serveo.net",
]

export const PRODUCTION_PANEL_BASE_URL = "https://painel.smartmedix.com.br"

export function isUnsafePanelUrl(value: string): boolean {
  try {
    const { hostname } = new URL(value)
    const normalizedHostname = hostname.toLowerCase()
    return (
      normalizedHostname === "localhost" ||
      normalizedHostname === "locahost" ||
      normalizedHostname === "127.0.0.1" ||
      normalizedHostname === "::1" ||
      TRANSIENT_PANEL_HOST_SUFFIXES.some((suffix) => normalizedHostname.endsWith(suffix))
    )
  } catch {
    return true
  }
}

/**
 * Hosts confiaveis para URLs do painel (webhook proativo + Evolution). Producao
 * sempre; um host extra so via PANEL_BASE_URL (opt-in DELIBERADO e dedicado —
 * nao reaproveita NEXT_PUBLIC_APP_URL/NEXTAUTH_URL, que ja vieram envenenados).
 */
function trustedPanelHosts(): Set<string> {
  const hosts = new Set<string>([new URL(PRODUCTION_PANEL_BASE_URL).hostname])
  const extra = process.env.PANEL_BASE_URL
  if (extra) {
    try {
      hosts.add(new URL(extra).hostname.toLowerCase())
    } catch {
      /* ignora valor invalido */
    }
  }
  return hosts
}

/**
 * Allowlist: so aceita URL https cujo host esteja explicitamente confiavel.
 * Qualquer outro (localhost, typo "locahost", ngrok, dev, http) e rejeitado.
 */
export function isTrustedPanelUrl(value: string): boolean {
  try {
    const url = new URL(value)
    if (url.protocol !== "https:") return false
    return trustedPanelHosts().has(url.hostname.toLowerCase())
  } catch {
    return false
  }
}

export function resolvePanelBaseUrl(req: NextRequest): string {
  const candidates = [
    req.headers.get("origin"),
    process.env.PANEL_BASE_URL,
    process.env.NEXT_PUBLIC_APP_URL,
    process.env.NEXTAUTH_URL,
    (() => {
      const host = req.headers.get("host")
      return host ? `https://${host}` : null
    })(),
  ]

  for (const candidate of candidates) {
    if (candidate && isTrustedPanelUrl(candidate)) {
      return candidate.replace(/\/$/, "")
    }
  }

  return PRODUCTION_PANEL_BASE_URL
}

export function normalizeEvolutionQrCode(rawQrCode?: unknown, mimeType = "image/png"): string | null {
  if (!rawQrCode) return null
  const raw = rawQrCode as any

  if (typeof raw === "string") {
    const trimmed = raw.trim()
    if (!trimmed) return null

    if (trimmed.startsWith("data:") || trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      return trimmed
    }

    const base64 = trimmed.replace(/\s/g, "")
    if (/^[A-Za-z0-9+/=]*$/.test(base64)) {
      return `data:${mimeType};base64,${base64}`
    }

    return null
  }

  if (Array.isArray(raw)) {
    for (const item of raw) {
      const normalized = normalizeEvolutionQrCode(item, mimeType)
      if (normalized) {
        return normalized
      }
    }
    return null
  }

  if (typeof raw !== "object") {
    return null
  }

  const qrCodeCandidates = [
    raw.base64,
    raw.qrcode,
    raw.qrCode,
    raw.qr,
    raw.data,
    raw.code,
    raw.qr_image,
  ]

  for (const candidate of qrCodeCandidates) {
    const normalized = normalizeEvolutionQrCode(candidate, mimeType)
    if (normalized) return normalized
  }

  return null
}

// Nome da instância Evolution: slug do assistente + sufixo do ID, para não
// colidir entre assistentes de clientes diferentes com o mesmo nome.
export function buildEvolutionInstanceName(assistantName: string, assistantId: unknown): string {
  const slug = String(assistantName || "assistente")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "assistente"

  return `${slug}-${String(assistantId).slice(-6)}`
}
